import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import type { DeepPartial, DefaultValues } from 'react-hook-form';

import { FormPanel } from '../../../components/global/FormPanel/FormPanel.tsx';
import { useI18n } from '../../../hooks/useI18n.ts';
import { About } from '../About/About.tsx';
import { CoverImage } from '../CoverImage/CoverImage.tsx';
import { Links } from '../Links/Links.tsx';
import { WhereAndWhen } from '../WhereAndWhen/WhereAndWhen.tsx';
import { schema } from './schema.ts';
import type { IncompleteEventFormComplete } from './schema.ts';

export const IncompleteEventForm: FC<{
  defaultValues: DefaultValues<IncompleteEventFormComplete>;
  onCurrentFormValueChange: (data: DeepPartial<IncompleteEventFormComplete>) => void;
  submitButton: ReactNode;
  onSubmit: (data: IncompleteEventFormComplete) => void;
}> = ({ defaultValues, onCurrentFormValueChange, submitButton, onSubmit }) => {
  const { t } = useI18n();

  const useFormValue = useForm<IncompleteEventFormComplete>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });

  useEffect(() => {
    const { unsubscribe } = useFormValue.watch((value) => {
      onCurrentFormValueChange(value);
    });
    return () => unsubscribe();
  }, [onCurrentFormValueChange, useFormValue, useFormValue.watch]);

  return (
    <Stack
      // @ts-expect-error noValidate not recognized by mantine form
      noValidate
      component="form"
      gap="xxs"
      w="100%"
      onSubmit={(event) => void useFormValue.handleSubmit(onSubmit)(event)}
    >
      <FormPanel name="about-event" title={t('event-form_section_title--about-event') ?? 'About event'}>
        <About useFormValue={useFormValue} />
      </FormPanel>
      <FormPanel name="where-and-when" title={t('event-form_section_title--when-and-where') ?? 'Where and when?'}>
        <WhereAndWhen useFormValue={useFormValue} />
      </FormPanel>
      <FormPanel name="cover-image" title={t('event-form_section_title--artwork') ?? 'Artwork'}>
        <CoverImage useFormValue={useFormValue} />
      </FormPanel>
      <FormPanel name="links" title={t('event-form_section_title--links') ?? 'Links'}>
        <Links useFormValue={useFormValue} />
      </FormPanel>
      {submitButton}
    </Stack>
  );
};
